import { PersonaProseTooltip } from '@/components/admin/personas/persona-prose-tooltip';
import { PersonaToneDialog } from '@/components/admin/personas/persona-tone-dialog';
import { Callout } from '@/components/ui/callout';
import { RequiredMark } from '@/components/ui/required-mark';
import type { PersonaDraft } from '@/lib/persona-draft';
import type {
  InvolvementLevel,
  PersonaPresentationKind,
  PersonaTone,
  PersonaToneEntry,
  PersonaToneWrite,
} from '@matter_extractor/shared';
import {
  INVOLVEMENT_LEVEL_DIRECTION_MAX,
  PERSONA_BACKGROUND_MAX,
  PERSONA_DIRECTION_MAX,
  PERSONA_FIRST_NAME_MAX,
  PERSONA_NAME_MAX,
  PERSONA_PRESENTATION_DIRECTION_MAX,
} from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';
import { Check, Plus, SquarePen, Trash2 } from 'lucide-react';
import { useId } from 'react';

/**
 * The left column of the editor: a name, a direction, a presentation, a
 * slider, a story and a tone (refonte modulaire §10.1; maquettes
 * `EditeurNomme`, `EditeurAnonyme`, `NouveauPersona`, `AjouterTon`).
 *
 * **AUTONOMOUS, and that is a requirement rather than a habit**: §13's guided
 * form will mount this component outside the editor, so it takes one callback
 * per field and holds no query, no mutation and no route of its own. The only
 * state it owns is which of its own boxes is open.
 *
 * The order is the mockup's and it does not move: nom, direction, présentation,
 * implication, histoire, ton. « L'histoire reste sous le slider, malgré sa
 * longueur, parce que l'ordre de saisie suit celui du bloc rendu à
 * l'interviewer (§10.3) et qu'avoir deux ordres pour la même chose coûte plus
 * cher que de descendre d'un cran dans la page. »
 *
 * ## Every field is the SAME WIDTH, and that costs a gutter
 *
 * The criterion's column settled this and the persona's inherits it: the
 * redefinition of a cran carries a trash can and the other fields do not, so
 * without a reserved 36 px the column would read as two columns. Two fields
 * escape it on purpose and the mockup says why: the **prénom**, because a
 * full-width box for « Claire » lies about what is expected.
 *
 * ## `readOnly` is « Voir », and it is not `disabled` - except on the radios
 *
 * A version opened read-only renders in the same shape, and the text fields
 * carry the `readonly` ATTRIBUTE so their content stays selectable and
 * scrollable (the criterion's column argues this at length). Radios have no
 * text to select and no `readonly` of their own, so they take `disabled`, which
 * is what the platform offers for « this choice is closed ».
 *
 * ## What is NOT here, and where it went
 *
 * - **« Proposer une histoire »**, the button beside « Son histoire »: it is
 *   served by the narrateur seat (§10.1) and it is issue #43. The field ships
 *   without it, and so does its note about the seat.
 * - **No `@` variable and no `{}` button** anywhere (§10.1): a persona plays in
 *   several experiences, and the matière of one session has no meaning in it.
 *   The experience's surcouche (§10.2) is where variables come back.
 * - **Nothing about « ne pas être une IA »** beyond the read-only note: that
 *   rule left the persona for the global rules of §11.3.
 */

/** The 36 px the trash can occupies, reserved so every field ends at the same x. */
function Gutter() {
  return <span aria-hidden="true" className="block w-9 shrink-0" />;
}

/*
  `read-only:` and not `disabled:` - see the header. The ground still greys, so
  a field one cannot type in still LOOKS like one, and the ink stays at
  `gray-700`, which clears the contrast floor rather than fading out of it.

  And NO WIDTH in this shared string, which is the rule `callout.tsx` and
  `admin-rail.tsx` both state from a real defect: it is concatenated with a
  per-field string into ONE class attribute, so two utilities of equal
  specificity are decided by Tailwind's stylesheet order and not by intent.
  Written here, `w-full` beat the `w-60` of the prénom and that field ran the
  whole column - visible on the first capture of this screen, and on nothing
  else. Every call site states its own width.
*/
const FIELD_BOX =
  'rounded-md border-2 border-gray-300 bg-white read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

/** One counter under a field: a bound of INPUT and not of meaning (H13). */
function Counter({ length, max }: { length: number; max: number }) {
  return (
    <p className="text-right text-gray-500 text-xs leading-4">
      {length} / {max} caractères
    </p>
  );
}

/** Where a cran's name sits under the track: the ends hug the ends (maquette). */
function nameAlignment(index: number, total: number): string {
  if (index === 0) return 'text-left';
  if (index === total - 1) return 'text-right';
  return 'text-center';
}

/** What the editor needs to run the « ajouter un ton » box, and nothing more. */
export interface PersonaToneCreation {
  readonly open: boolean;
  readonly busy: boolean;
  /** The refusal, worded by the caller from its `code` (H16). */
  readonly error: string | null;
  readonly onOpen: () => void;
  readonly onCancel: () => void;
  readonly onCreate: (input: PersonaToneWrite) => void;
}

export interface PersonaEditorFieldsProps {
  draft: PersonaDraft;
  /** The scale of §11.5: the slider's positions and each cran's current wording. */
  levels: readonly InvolvementLevel[];
  /** The tone library of §11.5, sorted by name - the tag cloud. */
  tones: readonly PersonaToneEntry[];
  /**
   * Has the library actually been READ? An empty list is otherwise ambiguous.
   *
   * `tones` is empty while the query is in flight, and stays empty if it fails.
   * Only this flag tells that apart from « the library really holds nothing »,
   * and the difference is a sentence that ACCUSES: a tone shown apart and
   * dashed, under « ne fait plus partie de la bibliothèque », while it is
   * sitting in it.
   */
  libraryRead: boolean;
  /** A version opened by « Voir », or a retired persona: nothing is typable. */
  readOnly: boolean;
  /** The rename refusal, worded by the caller from the server's `code`. */
  nameError: string | null;
  onName: (next: string) => void;
  /**
   * The name is committed on BLUR and not on every keystroke, because a rename
   * is its own write (`PATCH`) and not part of the draft autosave (§4).
   */
  onNameCommit: () => void;
  onDirection: (next: string) => void;
  onPresentationKind: (next: PersonaPresentationKind) => void;
  onFirstName: (next: string) => void;
  onSelfDirection: (next: string) => void;
  onLevel: (levelId: string) => void;
  /** Opens the redefinition, PRE-FILLED with the cran's current wording. */
  onRedefine: (prefill: string) => void;
  onOverrideDirection: (next: string) => void;
  /** The trash can: the redefinition goes, and the persona follows its cran again. */
  onDropOverride: () => void;
  onBackground: (next: string) => void;
  /** One tone or none - « les deux ensemble ou rien » (§10.1). */
  onTone: (next: PersonaTone | null) => void;
  toneCreation: PersonaToneCreation;
}

export function PersonaEditorFields({
  draft,
  levels,
  tones,
  libraryRead,
  readOnly,
  nameError,
  onName,
  onNameCommit,
  onDirection,
  onPresentationKind,
  onFirstName,
  onSelfDirection,
  onLevel,
  onRedefine,
  onOverrideDirection,
  onDropOverride,
  onBackground,
  onTone,
  toneCreation,
}: PersonaEditorFieldsProps) {
  const nameId = useId();
  const nameHintId = useId();
  const nameErrorId = useId();
  const directionId = useId();
  const directionHintId = useId();
  const firstNameId = useId();
  const firstNameHintId = useId();
  const selfDirectionId = useId();
  const selfDirectionHintId = useId();
  const presentationId = useId();
  const involvementId = useId();
  const overrideId = useId();
  const backgroundId = useId();
  const backgroundHintId = useId();
  const toneHintId = useId();

  const named = draft.presentation.kind === 'named';
  const level = levels.find((one) => one.id === draft.involvement.levelId) ?? null;
  /*
    A cran RETIRED from the scale (§10.1). The comparison is against the scale
    this component was handed, which is the same list the slider draws - so the
    note and the missing position can never disagree with each other.
  */
  const levelRetired = level === null;
  /** The frozen name is the only one a retired cran still has. */
  const levelName = level?.name ?? draft.involvement.name;
  /** The field is open when the operator redefined, and always on a retired cran. */
  const redefined = draft.involvement.overridden || levelRetired;
  const selectedTone = draft.tone;
  /*
    « Ce ton ne fait plus partie de la bibliothèque » is an ACCUSATION, so it
    waits for the library to have actually been read.

    Without `libraryRead`, an empty `tones` means « absent » - and `tones` is
    empty while the query is in flight, forever if it fails, and for one render
    right after the modal creates a tone (the caller invalidates and selects in
    the same breath). Each of those would have shown a perfectly ordinary tone
    dashed and apart, under a sentence saying it had been deleted.
  */
  const toneMissing =
    libraryRead && selectedTone !== null && !tones.some((tone) => tone.name === selectedTone.name);

  return (
    <div className="space-y-7">
      <section className="space-y-2">
        <label htmlFor={nameId} className="block font-semibold text-gray-800 text-sm">
          Nom du persona
          <RequiredMark />
        </label>
        <div className="flex items-start gap-2">
          <input
            id={nameId}
            data-testid="persona-nom"
            value={draft.name}
            readOnly={readOnly}
            required
            aria-required="true"
            aria-invalid={nameError !== null}
            // The hint or the refusal, never neither: a field whose rule is
            // only painted beside it has no rule for whoever cannot see it.
            aria-describedby={nameError === null ? nameHintId : nameErrorId}
            maxLength={PERSONA_NAME_MAX}
            placeholder="Un nom unique parmi les personas actifs"
            onChange={(event) => onName(event.currentTarget.value)}
            onBlur={onNameCommit}
            className={`${FIELD_BOX} h-11 w-full px-3 text-base`}
          />
          <Gutter />
        </div>
        {nameError === null ? (
          <p id={nameHintId} className="text-gray-600 text-xs leading-4">
            Unique parmi les personas actifs. C’est le nom côté opérateur : la personne ne le voit
            jamais. Renommer ne crée pas de version.
          </p>
        ) : (
          <p id={nameErrorId} role="alert" className="text-rouge-deep text-xs leading-4">
            {nameError}
          </p>
        )}
      </section>

      <section className="space-y-2">
        {/*
          The read-only note of §10.1, WORD FOR WORD, above the direction. It is
          what keeps an operator from rewriting « jamais IA » into every persona:
          the rule is global (§11.3), it holds for every persona and every
          session, and there is nothing to write here.

          A `Callout` and not a hand-drawn box: the maquette inks it grey with a
          left rule, which is exactly `tone="neutral"`, and this application has
          one callout so that a warning looks the same on every screen.

          « Réglages > Règles » is NOT a link, where the maquette draws one:
          §11.3's screen does not exist yet, `reglages-tabs.tsx` offers Modèles
          and Personas, and the typed router has no route to point at. A link to
          Réglages > Personas would land an operator on the involvement scale,
          which says nothing about « jamais IA » - a wrong destination is worse
          than none, and this one becomes a `<Link>` the day that lot lands.
        */}
        <Callout tone="neutral" data-testid="persona-note-regles">
          <strong className="font-semibold">Info :</strong> un persona garde son rôle de fiction
          même si la personne lui demande s’il est un robot. Voir les{' '}
          <strong className="font-semibold">règles globales (Réglages &gt; Règles)</strong> pour le
          détail.
        </Callout>

        <div className="space-y-0.5">
          <label htmlFor={directionId} className="block font-semibold text-gray-800 text-sm">
            Direction d’IA
            <RequiredMark />
          </label>
          <p id={directionHintId} className="text-gray-600 text-xs leading-4">
            Comment il se comporte. Servie en tête du bloc persona, à chaque tour.
          </p>
        </div>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-1.5">
            <textarea
              id={directionId}
              data-testid="persona-direction"
              value={draft.direction}
              readOnly={readOnly}
              required
              aria-required="true"
              aria-describedby={directionHintId}
              rows={6}
              maxLength={PERSONA_DIRECTION_MAX}
              placeholder="Comment il parle, ce qu’il cherche, ce qu’il ne fait pas."
              onChange={(event) => onDirection(event.currentTarget.value)}
              className={`${FIELD_BOX} min-h-38 w-full resize-y p-3 text-sm leading-relaxed`}
            />
            <Counter length={draft.direction.length} max={PERSONA_DIRECTION_MAX} />
          </div>
          <Gutter />
        </div>
      </section>

      {/*
        A `<fieldset>` and not a `<section>`: two radios sharing one label is
        exactly what a `<legend>` names, and it is what puts « Présentation » in
        front of « Anonyme » for a screen reader. The `<legend>` is the FIRST
        child, because a fieldset's content model is « optionally a legend,
        followed by flow content » - wrapped in a div it still renders and stops
        being the group's name.
      */}
      <fieldset className="min-w-0 space-y-2 border-0 p-0">
        <legend className="mb-1 block font-semibold text-gray-800 text-sm">
          Présentation
          <RequiredMark />
        </legend>
        <p className="text-gray-600 text-xs leading-4">Ce que la personne voit de lui.</p>

        <div className="flex items-stretch gap-3">
          <PresentationOption
            kind="anonymous"
            groupName={presentationId}
            title="Anonyme"
            description="Ni nom ni identité affichée ; il ne parle jamais de lui."
            checked={!named}
            disabled={readOnly}
            onSelect={onPresentationKind}
          />
          <PresentationOption
            kind="named"
            groupName={presentationId}
            title="Nommée"
            description="Un prénom dans l’en-tête et les bulles, plus une direction d’auto-présentation."
            checked={named}
            disabled={readOnly}
            onSelect={onPresentationKind}
          />
          <Gutter />
        </div>

        {named ? (
          <div className="space-y-5 pt-1">
            <div className="space-y-2">
              <label htmlFor={firstNameId} className="block font-semibold text-gray-800 text-sm">
                Prénom
                <RequiredMark />
              </label>
              {/*
                240 px, and one of the TWO fields the maquette lets out of the
                full width: « un champ pleine largeur pour "Claire" ment sur ce
                qu'on attend ». No gutter either - it does not reach the edge
                the others align on, so reserving one would be theatre.
              */}
              <input
                id={firstNameId}
                data-testid="persona-prenom"
                value={draft.presentation.firstName}
                readOnly={readOnly}
                required
                aria-required="true"
                aria-describedby={firstNameHintId}
                maxLength={PERSONA_FIRST_NAME_MAX}
                placeholder="Claire"
                onChange={(event) => onFirstName(event.currentTarget.value)}
                className={`${FIELD_BOX} h-11 w-60 px-3 text-base`}
              />
              <p id={firstNameHintId} className="text-gray-600 text-xs leading-4">
                Le seul nom que la personne voit. {PERSONA_FIRST_NAME_MAX} caractères.
              </p>
            </div>

            <div className="space-y-2">
              <div className="space-y-0.5">
                <label
                  htmlFor={selfDirectionId}
                  className="block font-semibold text-gray-800 text-sm"
                >
                  Direction d’auto-présentation
                  <RequiredMark />
                </label>
                <p id={selfDirectionHintId} className="text-gray-600 text-xs leading-4">
                  Servie <strong className="font-semibold">comme consigne</strong> au premier
                  message, jamais collée telle quelle : « dis en une phrase que tu travailles sur
                  les récits de métier », pas « Bonjour, je m’appelle Claire ».
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <textarea
                    id={selfDirectionId}
                    data-testid="persona-direction-presentation"
                    value={draft.presentation.direction}
                    readOnly={readOnly}
                    required
                    aria-required="true"
                    aria-describedby={selfDirectionHintId}
                    rows={3}
                    maxLength={PERSONA_PRESENTATION_DIRECTION_MAX}
                    placeholder="Dis en une phrase qui tu es et sur quoi tu travailles."
                    onChange={(event) => onSelfDirection(event.currentTarget.value)}
                    className={`${FIELD_BOX} min-h-23 w-full resize-y p-3 text-sm leading-relaxed`}
                  />
                  <Counter
                    length={draft.presentation.direction.length}
                    max={PERSONA_PRESENTATION_DIRECTION_MAX}
                  />
                </div>
                <Gutter />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 pt-1">
            {/*
              The dashed cartouche of the maquette, and it earns its place: « sans
              lui, l'opérateur croit avoir perdu sa saisie ». §10.1 keeps both
              fields in the draft when the presentation goes back to anonymous.
            */}
            <p
              data-testid="persona-presentation-cachee"
              className="min-w-0 flex-1 rounded-md border border-gray-300 border-dashed px-3.5 py-2.5 text-caption text-gray-600 italic"
            >
              Le prénom et la direction d’auto-présentation sont cachés en présentation anonyme. Le
              brouillon les garde : repasser en « nommée » les retrouve tels quels.
            </p>
            <Gutter />
          </div>
        )}
      </fieldset>

      <fieldset className="min-w-0 space-y-2 border-0 p-0">
        <legend className="mb-1 block font-semibold text-gray-800 text-sm">
          Degré d’implication
          <RequiredMark />
        </legend>
        <p className="text-gray-600 text-xs leading-4">
          L’échelle se règle dans{' '}
          <Link
            to="/admin/reglages/personas"
            className="text-bleu-nuit underline underline-offset-2"
          >
            Réglages &gt; Personas
          </Link>
          . Le cran donne sa direction au persona : elle suit la dernière rédaction du cran tant
          qu’elle n’est pas redéfinie ici.
        </p>

        <div className="flex items-start gap-2 pt-1">
          <div className="min-w-0 flex-1">
            {/*
              A radiogroup and not a `role="slider"` div, which is what the
              artboard drew because an artboard has no form controls (the same
              arbitrage `filter-panel.tsx` makes for its checkboxes). The scale
              is a handful of NAMED, discrete crans: real radios answer the arrow
              keys, report their state to assistive tech, and are what
              `getByRole('radio', { name })` finds. `aria-valuenow` on a div
              would have bought a keyboard handler and a worse announcement.
            */}
            <div
              data-testid="persona-slider"
              className="flex items-center"
              // The group's own name, since the `<legend>` above names the whole
              // section including the redefinition that follows it.
              role="radiogroup"
              aria-label="Degré d’implication"
            >
              {levels.map((one, index) => (
                <LevelPoint
                  key={one.id}
                  level={one}
                  index={index}
                  groupName={involvementId}
                  descriptionId={`${involvementId}-${index}`}
                  checked={one.id === draft.involvement.levelId}
                  /*
                    Filled up to and INCLUDING the active cran, and not at all
                    when the cran is retired: the gauge has no position to fill
                    towards then.

                    `<=` and not `<`, and the difference is the whole fill: the
                    segment `LevelPoint` draws sits BEFORE its own point, so the
                    one that arrives with index `i` spans the gap from point
                    `i - 1` to point `i`. It is behind the chosen cran whenever
                    `i` reaches it. Written `<`, the middle cran of a
                    three-cran scale inked NOTHING - a track that looked untouched
                    beside a point that was plainly chosen, on the first capture
                    of this screen.
                  */
                  filled={
                    !levelRetired &&
                    index <= levels.findIndex((other) => other.id === draft.involvement.levelId)
                  }
                  disabled={readOnly}
                  onSelect={onLevel}
                />
              ))}
            </div>
            <div className="mt-1.5 flex">
              {levels.map((one, index) => (
                <span
                  key={one.id}
                  className={`min-w-0 flex-1 truncate text-caption ${nameAlignment(index, levels.length)} ${
                    one.id === draft.involvement.levelId
                      ? 'font-semibold text-bleu-nuit'
                      : 'text-gray-600'
                  }`}
                >
                  {one.name}
                </span>
              ))}
            </div>
          </div>
          <Gutter />
        </div>

        {levelRetired ? (
          <div className="flex items-start gap-2 pt-2">
            <div className="min-w-0 flex-1">
              {/*
                At the WIDTH OF THE OTHER FIELDS, which the mockup review moved
                it to - it overflowed to the right before. It invalidates
                NOTHING (§10.1): not the persona, not its referents, not the
                sessions.
              */}
              <Callout
                tone="warn"
                data-testid="persona-cran-retire"
                title="Cran retiré du réglage, texte conservé."
              >
                Le cran « {levelName} » n’est plus dans l’échelle. Choisir un cran de l’échelle
                courante fera repartir le slider.
              </Callout>
            </div>
            <Gutter />
          </div>
        ) : null}

        {redefined ? (
          <div className="space-y-2 pt-2">
            <div className="flex items-start gap-2">
              <label
                htmlFor={overrideId}
                className="min-w-0 flex-1 font-semibold text-gray-800 text-sm"
              >
                {levelRetired
                  ? `Direction du cran « ${levelName} »`
                  : `Direction du cran « ${levelName} », redéfinie pour ce persona`}
              </label>
              <Gutter />
            </div>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1 space-y-1.5">
                <textarea
                  id={overrideId}
                  data-testid="persona-direction-cran"
                  value={draft.involvement.direction}
                  readOnly={readOnly}
                  rows={4}
                  maxLength={INVOLVEMENT_LEVEL_DIRECTION_MAX}
                  onChange={(event) => onOverrideDirection(event.currentTarget.value)}
                  className={`${FIELD_BOX} min-h-28 w-full resize-y p-3 text-sm leading-relaxed`}
                />
                <Counter
                  length={draft.involvement.direction.length}
                  max={INVOLVEMENT_LEVEL_DIRECTION_MAX}
                />
              </div>
              {/* NO trash can on a retired cran: there is no cran wording left
                  to come back to, so the gesture would lead nowhere (§10.1). */}
              {levelRetired || readOnly ? (
                <Gutter />
              ) : (
                <button
                  type="button"
                  data-testid="persona-retirer-redefinition"
                  aria-label="Retirer la redéfinition"
                  onClick={onDropOverride}
                  className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md border-2 border-gray-200 bg-white text-rouge hover:bg-rouge-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                </button>
              )}
            </div>
            <div className="flex items-start gap-2">
              <p className="min-w-0 flex-1 text-gray-600 text-xs leading-4">
                {levelRetired
                  ? 'Le cran n’existe plus dans l’échelle : le texte reste servi tel quel, et il n’y a plus de rédaction de cran où revenir. Rien n’est invalidé, ni le persona, ni ses référents, ni les sessions.'
                  : `La corbeille retire la redéfinition et remet le persona à la suite du cran : sa direction redeviendra celle de « ${levelName} », telle qu’elle sera rédigée au moment de l’enregistrement.`}
              </p>
              <Gutter />
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 pt-2">
            <div className="min-w-0 flex-1 space-y-1.5">
              {readOnly ? null : (
                <button
                  type="button"
                  data-testid="persona-redefinir-cran"
                  onClick={() => onRedefine(level?.direction ?? '')}
                  className="inline-flex cursor-pointer items-center gap-1.5 font-semibold text-bleu-nuit text-sm underline underline-offset-2 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <SquarePen aria-hidden="true" className="size-4" />
                  <span>Redéfinir la direction du cran</span>
                </button>
              )}
              <p className="text-gray-600 text-xs leading-4">
                Sans redéfinition, le persona suit la direction du cran : chaque nouvelle version
                recopie la rédaction courante de « {levelName} ».
              </p>
            </div>
            <Gutter />
          </div>
        )}
      </fieldset>

      <section className="space-y-2">
        <div className="space-y-0.5">
          <label htmlFor={backgroundId} className="block font-semibold text-gray-800 text-sm">
            Son histoire <span className="font-normal text-gray-600">(facultatif)</span>
          </label>
          <p id={backgroundHintId} className="text-gray-600 text-xs leading-4">
            Ce dans quoi il puise ses anecdotes quand son cran l’y autorise.
          </p>
        </div>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-1.5">
            <textarea
              id={backgroundId}
              data-testid="persona-histoire"
              value={draft.background}
              readOnly={readOnly}
              aria-describedby={backgroundHintId}
              rows={7}
              maxLength={PERSONA_BACKGROUND_MAX}
              placeholder="Ce dans quoi le persona peut puiser : un métier, un lieu, quelques souvenirs. Facultatif, et sans effet tant que le cran ne l’autorise pas."
              onChange={(event) => onBackground(event.currentTarget.value)}
              className={`${FIELD_BOX} min-h-44 w-full resize-y p-3 text-sm leading-relaxed`}
            />
            <Counter length={draft.background.length} max={PERSONA_BACKGROUND_MAX} />
          </div>
          <Gutter />
        </div>
      </section>

      <section className="space-y-2">
        <div className="space-y-0.5">
          <p className="block font-semibold text-gray-800 text-sm">
            Ton <span className="font-normal text-gray-600">(facultatif)</span>
          </p>
          <p id={toneHintId} className="text-gray-600 text-xs leading-4">
            Les tons de la bibliothèque. Un seul à la fois : choisir un ton remplace le précédent.
            Renommer, modifier le texte ou supprimer un ton se fait dans{' '}
            <Link
              to="/admin/reglages/personas"
              className="text-bleu-nuit underline underline-offset-2"
            >
              Réglages &gt; Personas
            </Link>
            .
          </p>
        </div>

        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-2.5">
            {/*
              A tone that has LEFT the library, shown apart and dashed (§10.1):
              the persona keeps its copy, and the screen has to say so without
              shouting. It is matched BY NAME because no provenance is kept at
              all - that is the decision, not an omission.
            */}
            {selectedTone !== null && toneMissing ? (
              <div className="flex flex-wrap items-center gap-2.5">
                <ToneTag
                  name={selectedTone.name}
                  text={selectedTone.text}
                  selected
                  missing
                  readOnly={readOnly}
                  onToggle={() => onTone(null)}
                />
                <span data-testid="persona-ton-absent" className="text-gray-600 text-xs leading-4">
                  ne fait plus partie de la bibliothèque ; le persona en garde sa copie
                </span>
              </div>
            ) : null}

            <div data-testid="persona-nuage-tons" className="flex flex-wrap gap-2">
              {readOnly ? (
                // The tone the persona HAS, and only it: an unpickable library
                // is noise on a screen made for reading. A tone shown as missing
                // is already in the row above, so this branch skips it.
                selectedTone === null || toneMissing ? null : (
                  <ToneTag
                    name={selectedTone.name}
                    text={selectedTone.text}
                    selected
                    missing={false}
                    readOnly
                    onToggle={() => undefined}
                  />
                )
              ) : (
                <>
                  {tones.map((tone) => (
                    <ToneTag
                      key={tone.id}
                      name={tone.name}
                      text={tone.text}
                      selected={selectedTone?.name === tone.name}
                      missing={false}
                      readOnly={false}
                      /* Clicking the one already chosen CLEARS it, and it is the
                         only way back to « sans ton » - a state the card words
                         explicitly, so it has to be reachable. Clicking any
                         other one replaces (§10.1). */
                      onToggle={() =>
                        onTone(
                          selectedTone?.name === tone.name
                            ? null
                            : { name: tone.name, text: tone.text },
                        )
                      }
                    />
                  ))}
                  <button
                    type="button"
                    data-testid="persona-ajouter-ton"
                    onClick={toneCreation.onOpen}
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border-2 border-gray-300 border-dashed bg-white px-3.5 py-1.5 font-semibold text-bleu-nuit text-sm leading-5 hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Plus aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
                    <span>ajouter un ton</span>
                  </button>
                </>
              )}
            </div>

            <p className="text-gray-600 text-xs leading-4">
              Le ton choisi est recopié dans la version. Modifier ou supprimer ce ton dans la
              bibliothèque ne change pas ce persona, et le persona ne sait pas d’où il vient.
            </p>
          </div>
          <Gutter />
        </div>
      </section>

      {toneCreation.open ? (
        <PersonaToneDialog
          busy={toneCreation.busy}
          error={toneCreation.error}
          onCreate={toneCreation.onCreate}
          onCancel={toneCreation.onCancel}
        />
      ) : null}
    </div>
  );
}

/** One of the two presentation radios, described rather than merely named. */
function PresentationOption({
  kind,
  groupName,
  title,
  description,
  checked,
  disabled,
  onSelect,
}: {
  kind: PersonaPresentationKind;
  /**
   * The radio group's name, from the mounting component's `useId()`.
   *
   * NOT a literal, for the reason this whole file exists: §13 will mount this
   * form outside the editor, and two literals of the same name in one document
   * are ONE native radio group - the two forms would then fight over which
   * presentation is checked. The slider four sections down already keys its own
   * group this way.
   */
  groupName: string;
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onSelect: (next: PersonaPresentationKind) => void;
}) {
  return (
    <label
      className={`flex flex-1 cursor-pointer items-start gap-2.5 rounded-md border-2 p-3.5 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-1 ${
        checked ? 'border-bleu-nuit bg-bleu-nuit-tint' : 'border-gray-200 bg-white'
      }`}
    >
      <input
        type="radio"
        name={groupName}
        data-testid={`persona-presentation-${kind}`}
        value={kind}
        checked={checked}
        disabled={disabled}
        onChange={() => onSelect(kind)}
        className="mt-0.5 size-4.5 accent-bleu-nuit"
      />
      <span className="min-w-0 space-y-0.5">
        <span
          className={`block font-semibold text-sm ${checked ? 'text-bleu-nuit' : 'text-gray-800'}`}
        >
          {title}
        </span>
        <span className="block text-gray-600 text-xs leading-4">{description}</span>
      </span>
    </label>
  );
}

/**
 * One point of the slider: a radio whose box is drawn, plus the cran's
 * direction in a bubble.
 *
 * The bubble is DRAWN (issue #104) and no longer the native `title` this file
 * carried. The old header argued the opposite - « a hand-drawn dark bubble
 * would be the one dark surface of a light-only application » - and its
 * premise was wrong rather than its logic: the design system draws the bubble
 * WHITE, and a `title`, drawn by the browser, comes out white on black on a
 * machine in dark theme, where no CSS of this repository can reach it.
 * `PersonaProseTooltip` is the one the tone tag mounts too.
 *
 * It hangs on the LABEL and not on the radio, which is where the `title` hung:
 * the label is the 26 px box the pointer meets, and leaving the radio alone
 * keeps its `aria-describedby` pointing at exactly one paragraph.
 *
 * A bubble only opens on a hover or a focus, so the SAME text is also an
 * `sr-only` paragraph the radio points at with `aria-describedby` - which makes
 * the direction reach assistive technology and a touch screen at all times,
 * rather than on a gesture they cannot perform.
 */
function LevelPoint({
  level,
  index,
  groupName,
  descriptionId,
  checked,
  filled,
  disabled,
  onSelect,
}: {
  level: InvolvementLevel;
  index: number;
  groupName: string;
  descriptionId: string;
  checked: boolean;
  filled: boolean;
  disabled: boolean;
  onSelect: (levelId: string) => void;
}) {
  return (
    <>
      {/* The segment BEFORE this point, which is how the fill is drawn without
          a computed width: one flexible bar per gap, inked when it is behind
          the chosen cran. */}
      {index === 0 ? null : (
        <span
          aria-hidden="true"
          className={`h-1.5 flex-1 rounded-full ${filled ? 'bg-bleu-nuit' : 'bg-rail'}`}
        />
      )}
      <PersonaProseTooltip text={level.direction}>
        <label className="relative flex size-6.5 shrink-0 cursor-pointer items-center justify-center">
          <input
            type="radio"
            name={groupName}
            data-testid="persona-cran"
            value={level.id}
            checked={checked}
            disabled={disabled}
            aria-describedby={descriptionId}
            onChange={() => onSelect(level.id)}
            /*
            COVERED, never `sr-only` - the lesson `asset-upload.tsx` states from
            BUILD-863 and BUILD-916: `sr-only` clips a CONTROL to one pixel, so
            the drawn point beside it intercepts every pointer event and the
            radio becomes unclickable by anything aiming at it. Found by
            `personas.spec.ts`, which could not check a cran. Transparent and
            stretched over the whole 26 px box, the input IS the target, and
            `peer-focus-visible` still rings the point that stands under it.
          */
            className="peer absolute inset-0 size-full cursor-pointer opacity-0"
          />
          {/* The radio's whole accessible NAME, and nothing else may join it: a
            label's text content is what a screen reader announces, so the
            direction below has to sit OUTSIDE this element or every cran would
            be announced as its name followed by a paragraph. */}
          <span className="sr-only">{level.name}</span>
          <span
            aria-hidden="true"
            className={`block rounded-full peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 ${
              checked
                ? 'size-6.5 border-4 border-bleu-nuit bg-white'
                : 'size-3.5 border-2 border-gray-300 bg-white'
            }`}
          />
        </label>
      </PersonaProseTooltip>
      {/* The DESCRIPTION, a sibling of the label AND of the bubble's trigger,
          for the reason just above - `Tooltip` clones its child rather than
          wrapping it, so this stays a sibling and the row keeps its shape.
          `sr-only` is absolutely positioned, so it takes no room in the row. */}
      <span id={descriptionId} className="sr-only">
        {level.direction}
      </span>
    </>
  );
}

/**
 * One tag of the tone cloud (§10.1).
 *
 * `missing` is the tone the persona kept after the library lost it: same
 * selected ink, a DASHED border, and the sentence beside it does the
 * explaining. Its text still comes from the persona's own copy, which is the
 * only place it exists any more.
 *
 * Its text is READ in a drawn bubble since issue #104 - the same
 * `PersonaProseTooltip` the slider's points mount - and the `sr-only` copy the
 * tag points at with `aria-describedby` stays beside it: a bubble opens on a
 * hover or a focus, and a touch screen offers neither.
 */
function ToneTag({
  name,
  text,
  selected,
  missing,
  readOnly,
  onToggle,
}: {
  name: string;
  text: string;
  selected: boolean;
  missing: boolean;
  readOnly: boolean;
  onToggle: () => void;
}) {
  const descriptionId = useId();
  const shape = `inline-flex items-center gap-1.5 rounded-full border-2 px-3.5 py-1.5 font-semibold text-sm leading-5 ${
    selected
      ? `bg-bleu-nuit-tint text-bleu-nuit ${missing ? 'border-dashed' : ''} border-bleu-nuit`
      : 'border-gray-200 bg-white text-gray-800'
  }`;

  const inside = (
    <>
      {selected ? <Check aria-hidden="true" className="size-3.5" strokeWidth={3} /> : null}
      <span>{name}</span>
    </>
  );

  /*
    The tone's TEXT, kept OUT of the tag: inside it, it would join the button's
    accessible name and be announced after every tag rather than described once.
    `sr-only` is absolutely positioned, so a sibling costs the cloud no gap.
  */
  const description = (
    <span id={descriptionId} className="sr-only">
      {text}
    </span>
  );

  /*
    BOTH renders carry the bubble, and that is the trap this tag sets: the tone
    of a version opened by « Voir » is a `<span>` and the tone of the cloud is a
    `<button>`, they both carried the native `title`, and dressing only the
    second would have left the read-only screen - the one made for reading -
    with no wording at all on a machine where a `title` comes out white on
    black. `PersonaProseTooltip` opens on hover for both, and on focus for the
    button, which is the only one of the two the keyboard reaches.
  */
  if (readOnly) {
    return (
      <>
        <PersonaProseTooltip text={text}>
          <span
            data-testid="persona-ton"
            data-selected="true"
            aria-describedby={descriptionId}
            className={shape}
          >
            {inside}
          </span>
        </PersonaProseTooltip>
        {description}
      </>
    );
  }

  return (
    <>
      <PersonaProseTooltip text={text}>
        <button
          type="button"
          data-testid="persona-ton"
          data-selected={selected ? 'true' : 'false'}
          aria-pressed={selected}
          aria-describedby={descriptionId}
          onClick={onToggle}
          className={`${shape} cursor-pointer hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        >
          {inside}
        </button>
      </PersonaProseTooltip>
      {description}
    </>
  );
}
